import { Head, Link, router } from "@inertiajs/react"
import {
  BookOpen,
  Plus,
  Pencil,
  Trash2,
  Clock,
  CheckCircle,
  AlertCircle,
  Eye,
  Globe,
  Lock,
} from "lucide-react"
import { useMemo, useState } from "react"
import { CoachLayout } from "../components/CoachLayout"
import { CoachGroupSwitcher } from "../components/CoachGroupSwitcher"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs"
import { CourseCoverImage } from "@/components/CourseCoverImage"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"

// ---------------------------------------------------------------------------
// Status Badge — combines `status` (draft/published) with `publicStatus`
// (private/pending_review/public/rejected) into a single coach-facing label.
// Mirrors the Articles status-filter UX, but the combined state is course-only.
// ---------------------------------------------------------------------------

function CourseStatusBadge({ course }) {
  if (course.status === "draft") {
    return (
      <Badge variant="secondary" className="bg-zinc-100 text-zinc-700">
        Draft
      </Badge>
    )
  }

  switch (course.publicStatus) {
    case "pending_review":
      return (
        <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
          <Clock className="h-3 w-3 mr-1" /> Pending Review
        </Badge>
      )
    case "public":
      return (
        <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
          <Globe className="h-3 w-3 mr-1" /> Public
        </Badge>
      )
    case "rejected":
      return (
        <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
          <AlertCircle className="h-3 w-3 mr-1" /> Rejected
        </Badge>
      )
    case "private":
    default:
      return (
        <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">
          <Lock className="h-3 w-3 mr-1" /> Published (Private)
        </Badge>
      )
  }
}

// ---------------------------------------------------------------------------
// My Courses — coach-authored, all states
// ---------------------------------------------------------------------------

function MyCourseCard({ course, onDelete }) {
  return (
    <Card className="overflow-hidden flex flex-col">
      <div className="aspect-[3/2] bg-zinc-100 relative">
        <CourseCoverImage
          url={course.coverImageUrl}
          alt={course.title}
          wrapperClassName="absolute inset-0 overflow-hidden"
          imgClassName="w-full h-full object-cover"
          fallbackIconClassName="h-10 w-10 text-zinc-300"
        />
      </div>
      <CardContent className="p-4 space-y-3 flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-medium text-zinc-900 line-clamp-2">{course.title}</h3>
          <CourseStatusBadge course={course} />
        </div>
        {course.category && (
          <p className="text-xs text-zinc-500">{course.category.name}</p>
        )}
        {course.description && (
          <p className="text-sm text-zinc-600 line-clamp-2">{course.description}</p>
        )}
        {course.publicStatus === "rejected" && course.rejectionReason && (
          <p className="text-xs text-red-600 mt-1">
            <span className="font-medium">Rejection:</span> {course.rejectionReason}
          </p>
        )}
        <p className="text-xs text-zinc-400">
          {course.lessonsCount} {course.lessonsCount === 1 ? "lesson" : "lessons"}
        </p>
        <div className="flex items-center gap-2 pt-2 mt-auto">
          <Button
            asChild
            variant="outline"
            size="sm"
            className="flex-1"
          >
            <Link href={`/app/coach/courses/${course.id}`}>
              <Eye className="h-4 w-4 mr-1" /> View
            </Link>
          </Button>
          <Button
            asChild
            variant="outline"
            size="sm"
          >
            <Link href={`/app/coach/courses/${course.id}/edit`}>
              <Pencil className="h-4 w-4" />
            </Link>
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
            onClick={() => onDelete(course)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Available Courses — admin courses + other coaches' approved-public courses
// ---------------------------------------------------------------------------

function AvailableCourseCard({ course, isAssigned, onAssign, onUnassign }) {
  return (
    <Card className="overflow-hidden flex flex-col">
      <div className="aspect-[3/2] bg-zinc-100 relative">
        <CourseCoverImage
          url={course.coverImageUrl}
          alt={course.title}
          wrapperClassName="absolute inset-0 overflow-hidden"
          imgClassName="w-full h-full object-cover"
          fallbackIconClassName="h-10 w-10 text-zinc-300"
        />
        {isAssigned && (
          <Badge className="absolute top-2 right-2 bg-green-500 text-white hover:bg-green-500">
            <CheckCircle className="h-3 w-3 mr-1" /> Assigned
          </Badge>
        )}
      </div>
      <CardContent className="p-4 space-y-3 flex-1 flex flex-col">
        <h3 className="font-medium text-zinc-900 line-clamp-2">{course.title}</h3>
        {course.category && (
          <p className="text-xs text-zinc-500">{course.category.name}</p>
        )}
        {course.createdBy && (
          <p className="text-xs text-zinc-400">
            By {course.createdBy.name}
          </p>
        )}
        {course.description && (
          <p className="text-sm text-zinc-600 line-clamp-2">{course.description}</p>
        )}
        <p className="text-xs text-zinc-400">
          {course.lessonsCount} {course.lessonsCount === 1 ? "lesson" : "lessons"}
        </p>
        <div className="flex items-center gap-2 pt-2 mt-auto">
          <Button
            asChild
            variant="outline"
            size="sm"
            className="flex-1"
          >
            <Link href={`/app/coach/courses/${course.id}`}>
              <Eye className="h-4 w-4 mr-1" /> View
            </Link>
          </Button>
          {isAssigned ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onUnassign(course)}
            >
              Unassign
            </Button>
          ) : (
            <Button
              variant="gradient"
              size="sm"
              onClick={() => onAssign(course)}
            >
              Assign
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export default function CoursesIndex({
  currentUser,
  myCourses = [],
  availableCourses = [],
  assignedCourseIds = [],
  coachGroup,
  coachGroups = [],
  pendingCount,
}) {
  const [deleteCourse, setDeleteCourse] = useState(null)

  // Default to "mine" when the coach has any of their own; otherwise drop to
  // "available" so the page isn't an empty state on first paint.
  const defaultTab = useMemo(() => {
    if (myCourses.length > 0) return "mine"
    if (availableCourses.length > 0) return "available"
    return "mine"
  }, [myCourses.length, availableCourses.length])

  const assignedSet = useMemo(
    () => new Set(assignedCourseIds || []),
    [assignedCourseIds]
  )

  const handleAssign = (course) => {
    router.post(
      `/app/coach/courses/${course.id}/assign`,
      {},
      { preserveScroll: true }
    )
  }

  const handleUnassign = (course) => {
    router.delete(
      `/app/coach/courses/${course.id}/unassign`,
      { preserveScroll: true }
    )
  }

  const handleDelete = () => {
    if (!deleteCourse) return
    router.delete(`/app/coach/courses/${deleteCourse.id}`, {
      preserveScroll: true,
      onSuccess: () => setDeleteCourse(null),
    })
  }

  return (
    <CoachLayout currentUser={currentUser}>
      <Head title="Courses - Kingdom Factor" />

      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-zinc-900">Courses</h1>
            <p className="text-zinc-600">
              Manage your own courses or assign others to{" "}
              {coachGroups?.length >= 1 ? (
                <CoachGroupSwitcher coachGroup={coachGroup} coachGroups={coachGroups} />
              ) : (
                <span className="font-medium">{coachGroup?.name || "your group"}</span>
              )}
              .
            </p>
          </div>
          <Button asChild variant="gradient">
            <Link href="/app/coach/courses/new">
              <Plus className="h-4 w-4 mr-1" />
              New Course
            </Link>
          </Button>
        </div>

        {/* Tabs */}
        <Tabs defaultValue={defaultTab}>
          <TabsList>
            <TabsTrigger value="mine">
              My Courses ({myCourses.length})
            </TabsTrigger>
            <TabsTrigger value="available">
              Available Courses ({availableCourses.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="mine" className="mt-6">
            {myCourses.length > 0 ? (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {myCourses.map((course) => (
                  <MyCourseCard
                    key={course.id}
                    course={course}
                    onDelete={setDeleteCourse}
                  />
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="text-center py-12 text-zinc-500">
                  <BookOpen className="h-12 w-12 mx-auto mb-3 text-zinc-300" />
                  <p className="font-medium text-zinc-700">
                    You haven't created any courses yet
                  </p>
                  <p className="text-sm">
                    Click "New Course" above to author your first one.
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="available" className="mt-6">
            {availableCourses.length > 0 ? (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {availableCourses.map((course) => (
                  <AvailableCourseCard
                    key={course.id}
                    course={course}
                    isAssigned={assignedSet.has(course.id)}
                    onAssign={handleAssign}
                    onUnassign={handleUnassign}
                  />
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="text-center py-12 text-zinc-500">
                  <BookOpen className="h-12 w-12 mx-auto mb-3 text-zinc-300" />
                  <p className="font-medium text-zinc-700">
                    No courses available yet
                  </p>
                  <p className="text-sm">
                    Admin-created and other coaches' approved-public courses will appear here.
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Delete confirmation */}
      <AlertDialog
        open={!!deleteCourse}
        onOpenChange={(open) => {
          if (!open) setDeleteCourse(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Course</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &ldquo;{deleteCourse?.title}&rdquo;?
              This will also remove all lessons, questions, and resources. This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete Course
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </CoachLayout>
  )
}
